import cx from "classnames";
import { graphql } from "gatsby";
import React, { FC, HTMLAttributes } from "react";

import { getBytesStr } from "../../lib/numberUtils";
import { IconSprite } from "../IconSprite";
import * as styles from "./DownloadFile.module.scss";
import { PdfData } from "./__generated__/PdfData";

const mimeStringMap: { [key: string]: string } = {
    // PDF
    "application/pdf": "PDF",

    // Text files
    "text/plain": "TXT",
    "text/rtf": "RTF",
    "application/rtf": "RTF",

    // Microsoft Office - Word
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",

    // Microsoft Office - PowerPoint
    "application/vnd.ms-powerpoint": "PPT",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",

    // Microsoft Office - Excel
    "application/vnd.ms-excel": "XLS",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",

    // OpenDocument formats
    "application/vnd.oasis.opendocument.formula": "ODF",
    "application/vnd.oasis.opendocument.graphics": "ODG",
    "application/vnd.oasis.opendocument.presentation": "ODP",
    "application/vnd.oasis.opendocument.spreadsheet": "ODS",
    "application/vnd.oasis.opendocument.text": "ODT",
    "application/vnd.oasis.opendocument.text-flat-xml": "FODT",
    "application/vnd.oasis.opendocument.spreadsheet-flat-xml": "FODS",
    "application/vnd.oasis.opendocument.presentation-flat-xml": "FODP",
    "application/vnd.oasis.opendocument.graphics-flat-xml": "FODG",

    // Apple iWork
    "application/vnd.apple.keynote": "KEY",
    "application/vnd.apple.numbers": "NUMBERS",
    "application/vnd.apple.pages": "PAGES",

    // vCard
    "text/vcard": "VCF",
    "text/x-vcard": "VCF",

    default: "",
};


type Variant = "main" | "sidebar" | "locked" | "unlocked";

interface Props extends HTMLAttributes<HTMLElement> {
	pdf?: Array<PdfData | null> | null;
	variant?: Variant;
    label?: string;
}

interface AnchorOrSpanProps extends HTMLAttributes<HTMLElement> {
	href: string;
	variant?: Variant;
}

interface LockedStateProps {
	variant: Variant;
}

const AnchorOrSpan: FC<AnchorOrSpanProps> = ({
	className,
	href,
	children,
	variant = "main",
}) =>
	variant === "locked" ? (
		<span className={cx(styles.component, styles[variant], className)}>
			{children}
		</span>
	) : (
		<a
			download
			className={cx(styles.component, styles[variant], className)}
			href={href}
			target="_blank"
			rel="noopener noreferrer"
		>
			{children}
		</a>
	);

const LockedState: FC<LockedStateProps> = ({ variant }) =>
	["locked", "unlocked"].includes(variant) ? (
		<span className={styles.lockedState}>
			<IconSprite
				className={styles.lockedStateIcon}
				name={variant === "locked" ? "lock" : "unlocked"}
			/>
			<span>{variant}</span>
		</span>
	) : null;

export const DownloadFile: FC<Props> = ({ variant = "main", pdf, ...rest }) => {
	const file = pdf?.[0];
	const href = file?.fields?.cdn_url;
    // Use downloadMetadata.label if available, otherwise use rest.label, otherwise default
    const label = (file as any)?.downloadMetadata?.label || (file as any)?.downloadMetadata?.name || rest.label || 'Read Full Report';
	if (!file || !href) {
		return null;
	}

	const mimeStr = mimeStringMap[file.filemime ?? "default"] || "";

	return (
		<AnchorOrSpan
			href={href}
			variant={variant}
			className={styles[variant]}
			{...rest}
		>
			<IconSprite className={styles.icon} name="pdf" />
			<div className={styles.textWrapper}>
				<span className={styles.downloadText}>{label}</span>
				<span className={styles.metadataText}>{`(${mimeStr} ${file?.filesize && getBytesStr(file?.filesize)})`}</span>
			</div>

			<LockedState variant={variant} />
		</AnchorOrSpan>
	);
};

export const fragment = graphql`
    fragment PdfData on file__imagekit {
        id
        filename
        filesize
        filemime
        fields {
            cdn_url
        }
    }
`;

export default DownloadFile;
